import { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { dataVersionState } from '../../state.js';
import { ConfirmDialog } from '../ConfirmDialog.js';
import { ErrorBanner, Spinner } from '../FormBits.js';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js';
import { fmtLimit, fmtValue } from '../PolicyControlTable.js';
import { cn, fmtIls, fmtMonthLong, fmtNum } from '../../utils.js';
import { Table, THeadRow, TH, TD, TR, Ltr } from '../ui/table.js';
import { policy, type AdminPolicyReport } from './policyApi.js';
import type { PolicyReportRow, PolicyResultRow } from '@agio/core/domain';

/**
 * One month of policy control: compute → look → fill the gaps → publish.
 *
 * Nothing here reaches the client until פרסום. A line without a value blocks
 * publishing until an admin types one in; a breach blocks it until an admin
 * ticks that they have seen it. The list of funds the evaluation ran over is
 * shown before anything else, because "which funds does this include" is the
 * first question anyone asks of a compliance table.
 */
export function PolicyMonthTab({ clientId }: { clientId: string }) {
  const bump = useSetRecoilState(dataVersionState);
  const [periods, setPeriods] = useState<string[]>([]);
  const [reports, setReports] = useState<PolicyReportRow[]>([]);
  const [period, setPeriod] = useState<string>('');
  const [view, setView] = useState<AdminPolicyReport | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<'compute' | 'publish' | null>(null);

  useEffect(() => {
    let live = true;
    setView(null);
    policy.months(clientId).then((m) => {
      if (!live) return;
      setPeriods(m.periods);
      setReports(m.reports);
      setPeriod((p) => (p && m.periods.includes(p) ? p : m.periods[0] ?? ''));
    }).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId]);

  useEffect(() => {
    if (!period) { setView(null); return; }
    let live = true;
    setError(null);
    policy.report(clientId, period).then((v) => live && setView(v)).catch((e: Error) => live && setError(e.message));
    return () => { live = false; };
  }, [clientId, period]);

  const act = async (what: string, fn: () => Promise<AdminPolicyReport | null | void>) => {
    setBusy(what);
    setError(null);
    try {
      // Acknowledging returns nothing; the line's tick still has to show, so re-read the month.
      setView((await fn()) ?? (await policy.report(clientId, period)));
      setReports((await policy.months(clientId)).reports);
      bump((x) => x + 1);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const reportOf = (p: string) => reports.find((r) => r.period === p);
  const statusLabel = (r?: PolicyReportRow) => (!r ? 'טרם חושב' : r.status === 'published' ? 'פורסם' : 'טיוטה');

  return (
    <div className="grid gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">חודש</span>
          <Select dir="rtl" value={period} onValueChange={setPeriod}>
            <SelectTrigger className="min-w-44 bg-popover"><SelectValue placeholder="בחרו חודש…" /></SelectTrigger>
            <SelectContent>
              {periods.map((p) => (
                <SelectItem key={p} value={p}>{fmtMonthLong(p)} · {statusLabel(reportOf(p))}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        {period && (
          <button
            type="button"
            disabled={!!busy}
            onClick={() => (view?.report.status === 'published' ? setConfirm('compute') : void act('compute', () => policy.compute(clientId, period)))}
            className="flex items-center gap-2 rounded-md border border-border px-4 py-1.5 text-sm font-medium hover:bg-muted disabled:opacity-60"
          >
            {busy === 'compute' && <Spinner />} {view ? 'חשב מחדש' : 'חשב'}
          </button>
        )}
        {view && view.report.status === 'draft' && (
          <button
            type="button"
            disabled={!!busy || view.blockers.length > 0}
            onClick={() => setConfirm('publish')}
            className="flex items-center gap-2 rounded-md bg-primary px-5 py-1.5 text-sm font-semibold text-primary-foreground disabled:opacity-50"
            title={view.blockers.length ? 'יש שורות שחוסמות פרסום' : undefined}
          >
            {busy === 'publish' && <Spinner />} פרסום ללקוח
          </button>
        )}
        {view && view.report.status === 'published' && (
          <>
            <span className="rounded-full bg-success/15 px-3 py-1 text-xs font-semibold text-success">
              פורסם {view.report.published_at ? new Date(view.report.published_at).toLocaleDateString('he-IL') : ''}
            </span>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => void act('unpublish', async () => { await policy.unpublish(clientId, period); return policy.report(clientId, period); })}
              className="text-sm text-muted-foreground hover:underline"
            >
              הסרה מהפורטל
            </button>
          </>
        )}
      </div>
      {periods.length === 0 && <p className="text-sm text-muted-foreground">אין עדיין תמונות מצב חודשיות ללקוח זה — קלטו דוח חודשי קודם.</p>}
      <ErrorBanner error={error} title="שגיאה" />

      {view && (
        <>
          <BreachBanner view={view} />
          <FundsIncluded view={view} />
          {view.results.length > 0 && view.blockers.length > 0 && view.report.status === 'draft' && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
              <p className="font-semibold">לפני פרסום:</p>
              <ul className="mt-1 list-disc ps-5">
                {view.blockers.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </div>
          )}
          <ResultsTable view={view} busy={!!busy} onChange={(fn) => void act('edit', fn)} />
        </>
      )}
      {period && !view && !error && (
        <p className="text-sm text-muted-foreground">עדיין לא חושבה בקרת מדיניות לחודש זה. לחצו "חשב".</p>
      )}

      <ConfirmDialog
        open={confirm === 'compute'}
        title="לחשב מחדש חודש שפורסם?"
        confirmLabel="חשב מחדש"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); void act('compute', () => policy.compute(clientId, period)); }}
      >
        <p>החישוב המחודש יחליף את המספרים שהלקוח רואה, והחודש יחזור למצב טיוטה עד שיפורסם שוב. ערכים שהוזנו ידנית נשמרים.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === 'publish'}
        title="לפרסם ללקוח?"
        confirmLabel="פרסום"
        onCancel={() => setConfirm(null)}
        onConfirm={() => { setConfirm(null); void act('publish', () => policy.publish(clientId, period)); }}
      >
        <p>דו"ח בקרת המדיניות לחודש {period && fmtMonthLong(period)} יופיע בפורטל ובדוח המודפס של הלקוח, עבור {view?.report.funds.length ?? 0} תיקים.</p>
      </ConfirmDialog>
    </div>
  );
}

/** The first thing to know about a month: is the portfolio off policy, and where. */
function BreachBanner({ view }: { view: AdminPolicyReport }) {
  const breaches = view.results.filter((r) => r.status === 'breach');
  const noData = view.results.filter((r) => r.status === 'no_data' && r.fund_id !== null);
  if (breaches.length === 0 && noData.length === 0) {
    return (
      <div className="rounded-lg border border-success/40 bg-success/10 p-4 text-sm">
        <span className="font-semibold text-success">התיק עומד במדיניות</span> — כל הכללים בתחום המגבלות בחודש זה.
      </div>
    );
  }
  return (
    <div className="grid gap-3">
      {breaches.length > 0 && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm">
          <p className="font-semibold text-destructive">
            התיק חורג מהמדיניות ב-{breaches.length === 1 ? 'שורה אחת' : `${breaches.length} שורות`}:
          </p>
          <ul className="mt-1 list-disc ps-5">
            {breaches.map((r) => (
              <li key={r.result_id}>
                {r.label_he} · {r.fund ?? 'סיכום'}: {fmtValue(r.unit, r.effective)} מול {fmtLimit(r)} (הפרש {fmtValue(r.unit, r.diff, 1)})
              </li>
            ))}
          </ul>
        </div>
      )}
      {noData.length > 0 && (
        <div className="rounded-lg border border-warning/40 bg-warning/10 p-4 text-sm">
          <span className="font-semibold">{noData.length === 1 ? 'שורה אחת' : `${noData.length} שורות`} ללא נתון</span> — יש להזין ערך ידני לפני פרסום.
        </div>
      )}
    </div>
  );
}

function FundsIncluded({ view }: { view: AdminPolicyReport }) {
  const r = view.report;
  return (
    <div className="grid gap-4 rounded-lg border border-border p-4 text-sm">
      <div>
        <p className="mb-1 font-semibold">התיקים שנכללו ({r.funds.length})</p>
        <Table minWidth="28rem">
          <THeadRow>
            <TH>תיק</TH><TH>תאריך הדוח</TH><TH align="end">שווי</TH><TH align="end">ניירות ערך</TH>
          </THeadRow>
          <tbody>
            {r.funds.map((f) => (
              <TR key={f.fund_id}>
                <TD className="py-2 font-medium">{f.fund}</TD>
                <TD className="py-2"><Ltr>{f.report_date}</Ltr></TD>
                <TD align="end" className="py-2 tabular-nums">{fmtIls(f.total_value)}</TD>
                <TD align="end" className="py-2">
                  {f.positions_count
                    ? <span className="tabular-nums">{fmtNum(f.positions_count)}</span>
                    : <span className="text-warning" title="מדדים המבוססים על פירוט ניירות (מט&quot;ח, דירוג, מח&quot;מ) יסומנו 'אין נתון'">ללא פירוט</span>}
                </TD>
              </TR>
            ))}
          </tbody>
        </Table>
      </div>
      {r.funds_without_snapshot.length > 0 && (
        <div>
          <p className="mb-1 font-semibold">
            לא נכללו ({r.funds_without_snapshot.length})
            <span className="ms-2 font-normal text-muted-foreground">אין להם תמונת מצב לחודש זה</span>
          </p>
          <Table minWidth="20rem">
            <THeadRow>
              <TH>תיק</TH><TH>תמונת מצב אחרונה</TH>
            </THeadRow>
            <tbody>
              {r.funds_without_snapshot.map((f) => (
                <TR key={f.fund_id}>
                  <TD className="py-1.5">{f.fund}</TD>
                  <TD className="py-1.5 text-muted-foreground">{f.last_date ? <Ltr>{f.last_date}</Ltr> : 'אין'}</TD>
                </TR>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <p className="text-xs text-muted-foreground">
        חושב {new Date(r.computed_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' })} על ידי {r.computed_by}
      </p>
    </div>
  );
}

function ResultsTable({ view, busy, onChange }: {
  view: AdminPolicyReport; busy: boolean; onChange: (fn: () => Promise<AdminPolicyReport | void>) => void;
}) {
  const groups = new Map<string, PolicyResultRow[]>();
  for (const r of view.results) (groups.get(r.rule_id) ?? groups.set(r.rule_id, []).get(r.rule_id)!).push(r);
  if (groups.size === 0) return <p className="text-sm text-muted-foreground">אין כללי מדיניות בתוקף לחודש זה — הגדירו כללים בלשונית "כללי המדיניות".</p>;
  const editable = view.report.status === 'draft';
  return (
    <div className="grid gap-6">
      {[...groups.values()].map((rows) => (
        <div key={rows[0].rule_id} className="overflow-x-auto">
          <table className="w-full text-sm" style={{ minWidth: '44rem' }}>
            <thead>
              <tr className="border-b border-border text-right text-muted-foreground">
                <th className="py-2 pe-2 text-base font-semibold text-foreground">{rows[0].label_he}</th>
                <th className="px-2 py-2 text-center font-normal">מגבלת מדיניות</th>
                <th className="px-2 py-2 text-center font-normal">מצב בפועל</th>
                <th className="px-2 py-2 text-center font-normal">הפרש</th>
                <th className="px-2 py-2 text-right font-normal">הערות</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => <ResultLine key={r.result_id} r={r} editable={editable} busy={busy} onChange={onChange} />)}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

function ResultLine({ r, editable, busy, onChange }: {
  r: PolicyResultRow; editable: boolean; busy: boolean; onChange: (fn: () => Promise<AdminPolicyReport | void>) => void;
}) {
  const [manual, setManual] = useState(r.manual_actual === null ? '' : r.unit === 'pct' ? String(+(r.manual_actual * 100).toFixed(4)) : String(r.manual_actual));
  const [note, setNote] = useState(r.manual_note ?? '');
  // A computed value can be wrong too (a statement that mislabels a fund, say);
  // the override is always available, just folded away when nothing needs it.
  const [overriding, setOverriding] = useState(false);
  const isSummary = r.fund_id === null;
  const showEditor = !isSummary && editable && (r.status === 'no_data' || r.manual_actual !== null || overriding);
  const tone = r.status === 'breach' ? 'bg-destructive/15 text-destructive' : r.status === 'ok' ? 'bg-success/15 text-success' : 'text-muted-foreground';
  const save = () => {
    const t = manual.trim();
    const v = t === '' ? null : r.unit === 'pct' ? Number(t) / 100 : Number(t);
    onChange(() => policy.setManual(r.result_id, v, note));
  };
  return (
    <tr className={cn('border-b border-border/50', isSummary && 'bg-white/5 font-semibold')}>
      <td className="py-2 pe-2">{isSummary ? 'סיכום' : r.fund}</td>
      <td className="px-2 py-2 text-center tabular-nums">{fmtLimit(r)}</td>
      <td className="px-2 py-2 text-center tabular-nums">
        {fmtValue(r.unit, r.effective)}
        {r.manual_actual !== null && <span className="ms-1 text-xs text-warning" title={`חושב: ${fmtValue(r.unit, r.actual)}`}>ידני</span>}
      </td>
      <td className={cn('px-2 py-2 text-center tabular-nums font-semibold', tone)}>
        {r.status === 'no_data' ? 'אין נתון' : fmtValue(r.unit, r.diff, 1)}
      </td>
      <td className="px-2 py-2 text-xs">
        {r.status === 'no_data' && <p className="text-warning">{r.inputs.missing}</p>}
        {r.status !== 'no_data' && r.inputs.source_he && <p className="text-muted-foreground">מקור: {r.inputs.source_he}</p>}
        {r.inputs.note_he && <p className="text-warning">{r.inputs.note_he}</p>}
        {!isSummary && editable && !showEditor && (
          <button type="button" className="text-muted-foreground hover:underline" onClick={() => setOverriding(true)}>
            הזנה ידנית במקום הערך המחושב
          </button>
        )}
        {showEditor && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              dir="ltr"
              inputMode="decimal"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder={r.unit === 'pct' ? '% ידני' : 'שנים'}
              className="w-24 rounded border border-border bg-popover px-2 py-1"
            />
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="מקור הערך (למשל: דוח הבנק, עמ' 4)"
              className="w-56 rounded border border-border bg-popover px-2 py-1"
            />
            <button type="button" disabled={busy} onClick={save} className="rounded bg-primary px-3 py-1 font-semibold text-primary-foreground disabled:opacity-60">שמירה</button>
          </div>
        )}
        {r.manual_note && <p className="mt-1 text-muted-foreground">{r.manual_note}</p>}
        {r.status === 'breach' && editable && (
          <label className="mt-1 flex items-center gap-2">
            <input type="checkbox" checked={r.acknowledged} disabled={busy} onChange={(e) => onChange(async () => { await policy.acknowledge(r.result_id, e.target.checked); })} />
            ראיתי את החריגה — ניתן לפרסם
          </label>
        )}
        {r.status === 'breach' && !editable && <span className="text-muted-foreground">חריגה מאושרת</span>}
      </td>
    </tr>
  );
}
